import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Trash2, CheckCheck, Circle, Archive, ArchiveRestore, Copy, ExternalLink, Loader2, Inbox } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

function fmt(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
}

export default function AdminFeedback() {
  const [items, setItems] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/feedback/admin`, { params: { include_archived: showArchived } });
      setItems(r.data);
    } catch (e) {
      if (e?.response?.status === 403) toast.error("Admin access required");
      else toast.error("Failed to load feedback");
      setItems([]);
    }
  };

  useEffect(() => { load(); }, [showArchived]);

  const patch = async (id, changes) => {
    try {
      await axios.patch(`${API}/feedback/admin/${id}`, changes);
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const onDelete = async (fb) => {
    // Optimistic remove + 10s undo (server-side hard delete after 10s)
    setItems((prev) => prev.filter((x) => x.id !== fb.id));
    let undone = false;
    toast("Feedback deleted", {
      duration: 10000,
      action: {
        label: "Undo",
        onClick: () => { undone = true; load(); },
      },
    });
    setTimeout(async () => {
      if (undone) return;
      try {
        await axios.delete(`${API}/feedback/admin/${fb.id}`);
      } catch {
        toast.error("Delete failed on server");
        load();
      }
    }, 10000);
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text).then(
      () => toast.success("Copied to clipboard"),
      () => toast.error("Copy failed"),
    );
  };

  const unreadCount = (items || []).filter((x) => !x.is_read && !x.is_archived).length;

  return (
    <div className="space-y-8" data-testid="admin-feedback-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-amber-600 font-semibold">Admin</div>
          <h1 className="mt-1 font-display text-3xl font-bold flex items-center gap-3">
            Feedback inbox
            {unreadCount > 0 && (
              <span className="text-sm font-medium bg-amber-100 text-amber-800 rounded-full px-2.5 py-0.5" data-testid="unread-badge">
                {unreadCount} unread
              </span>
            )}
          </h1>
          <p className="mt-1 text-stone-600 text-sm">Anonymous submissions from the site footer.</p>
        </div>
        <div className="flex items-center gap-2 bg-white border border-stone-200 rounded-full px-4 py-2">
          <Label htmlFor="show-archived" className="text-sm text-stone-600 cursor-pointer">Show archived</Label>
          <Switch id="show-archived" checked={showArchived} onCheckedChange={setShowArchived} data-testid="show-archived-toggle" />
        </div>
      </div>

      {items === null ? (
        <div className="text-stone-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-stone-300 bg-white p-10 text-center text-stone-500" data-testid="empty-feedback">
          <Inbox className="w-8 h-8 mx-auto mb-2 text-stone-400" />
          {showArchived ? "No archived feedback yet." : "No feedback yet. Nothing to see here — for now."}
        </div>
      ) : (
        <div className="grid gap-4" data-testid="feedback-list">
          {items.map((fb) => (
            <div
              key={fb.id}
              className={`bg-white border rounded-2xl p-5 ${fb.is_archived ? "border-stone-200 opacity-60" : fb.is_read ? "border-stone-200" : "border-amber-300 shadow-sm"}`}
              data-testid={`feedback-row-${fb.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap text-xs">
                    {!fb.is_read && !fb.is_archived && (
                      <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-800 border border-amber-100 rounded-full px-2 py-0.5 font-semibold uppercase tracking-[0.12em]">
                        <Circle className="w-2 h-2 fill-current" /> New
                      </span>
                    )}
                    {fb.is_archived && (
                      <span className="bg-stone-100 text-stone-600 border border-stone-200 rounded-full px-2 py-0.5 font-semibold uppercase tracking-[0.12em]">Archived</span>
                    )}
                    <span className="text-stone-500">{fmt(fb.created_at)}</span>
                    {fb.path && (
                      <span className="text-stone-400">·  from <code className="text-stone-600">{fb.path}</code></span>
                    )}
                  </div>
                  <p className="mt-3 text-stone-800 whitespace-pre-wrap leading-relaxed" data-testid={`feedback-message-${fb.id}`}>
                    {fb.message}
                  </p>
                  <div className="mt-3 flex items-center gap-4 flex-wrap text-xs text-stone-500">
                    {fb.contact_email ? (
                      <a
                        href={`mailto:${fb.contact_email}?subject=Re: your HomeSchool Hub feedback`}
                        className="inline-flex items-center gap-1.5 text-amber-700 hover:text-amber-900 font-medium"
                        data-testid={`feedback-reply-${fb.id}`}
                      >
                        <Mail className="w-3.5 h-3.5" /> Reply → {fb.contact_email}
                      </a>
                    ) : (
                      <span className="italic text-stone-400">No email provided</span>
                    )}
                    {fb.user_agent && (
                      <span className="truncate max-w-[24rem]" title={fb.user_agent}>{fb.user_agent}</span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1.5 flex-none">
                  {!fb.is_read ? (
                    <Button size="sm" variant="ghost" onClick={() => patch(fb.id, { is_read: true })} className="rounded-full text-xs" data-testid={`mark-read-${fb.id}`}>
                      <CheckCheck className="w-3.5 h-3.5 mr-1" /> Mark read
                    </Button>
                  ) : (
                    <Button size="sm" variant="ghost" onClick={() => patch(fb.id, { is_read: false })} className="rounded-full text-xs text-stone-500" data-testid={`mark-unread-${fb.id}`}>
                      <Circle className="w-3.5 h-3.5 mr-1" /> Mark unread
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => copy(fb.message)} className="rounded-full text-xs" data-testid={`copy-${fb.id}`}>
                    <Copy className="w-3.5 h-3.5 mr-1" /> Copy
                  </Button>
                  {fb.is_archived ? (
                    <Button size="sm" variant="ghost" onClick={() => patch(fb.id, { is_archived: false })} className="rounded-full text-xs" data-testid={`unarchive-${fb.id}`}>
                      <ArchiveRestore className="w-3.5 h-3.5 mr-1" /> Unarchive
                    </Button>
                  ) : (
                    <Button size="sm" variant="ghost" onClick={() => patch(fb.id, { is_archived: true, is_read: true })} className="rounded-full text-xs" data-testid={`archive-${fb.id}`}>
                      <Archive className="w-3.5 h-3.5 mr-1" /> Archive
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => onDelete(fb)} className="rounded-full text-xs text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`delete-${fb.id}`}>
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                  </Button>
                  {fb.path && (
                    <a href={fb.path} target="_blank" rel="noreferrer" className="text-[11px] text-stone-400 hover:text-stone-700 inline-flex items-center gap-1 mt-1">
                      Open page <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
